import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, Calendar, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { getLeaveRequests, createLeaveRequest, updateLeaveRequest } from '../../services/api';
import { useUnread } from '../../context/UnreadContext';
import { UnreadDot } from '../../components/dashboard/UnreadBadge';

export const LeaveRequestsPage = () => {
  const { user, can, isAdmin } = useAuth();
  // Vue d'équipe : approuver les congés (ou les voir tous).
  const canApprove = can('leave_requests.approve');
  const teamView = canApprove || can('leave_requests.view', 'all');
  const canRequestLeave = can('leave_requests.create') && !isAdmin;
  const { isUnread, markRead } = useUnread();
  // La liste montre tout le contenu d'une demande : l'afficher suffit à la lire.
  // Les demandes nouvelles gardent leur point rouge pendant cette visite.
  const [newThisVisit, setNewThisVisit] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    if (!canApprove) return;
    const unreadIds = requests.map(request => request.id).filter(id => isUnread('leave_requests', id));
    if (unreadIds.length === 0) return;
    setNewThisVisit(current => [...new Set([...current, ...unreadIds])]);
    markRead('leave_requests', unreadIds);
  }, [canApprove, requests, isUnread, markRead]);

  const fetchRequests = async () => {
    try {
      const response = await getLeaveRequests();
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createLeaveRequest(formData);
      toast.success('Demande de congé envoyée');
      setFormOpen(false);
      resetForm();
      fetchRequests();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const handleAction = async (requestId, status) => {
    try {
      await updateLeaveRequest(requestId, { status });
      toast.success(`Demande ${status === 'approved' ? 'approuvée' : 'refusée'}`);
      fetchRequests();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const resetForm = () => {
    setFormData({
      start_date: '',
      end_date: '',
      reason: ''
    });
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Stats
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const myPendingCount = requests.filter(r => r.status === 'pending' && r.employee_id === user?.id).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leave-requests-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]">Demandes de congé</h1>
          <p className="text-slate-500">
            {teamView ? 'Gérez les demandes de congé des employés' : 'Gérez vos demandes de congé'}
          </p>
        </div>
        {canRequestLeave && (
          <Button 
            onClick={() => {
              resetForm();
              setFormOpen(true);
            }}
            className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8]"
            data-testid="request-leave-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Demander un congé
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1a3a5c]">
                  {teamView ? pendingCount : myPendingCount}
                </p>
                <p className="text-sm text-slate-500">En attente</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1a3a5c]">{approvedCount}</p>
                <p className="text-sm text-slate-500">Approuvés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {canRequestLeave && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a3a5c]">{user?.vacation_days || 0}</p>
                  <p className="text-sm text-slate-500">Jours restants</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Requests List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {teamView ? 'Toutes les demandes' : 'Mes demandes'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {requests.length > 0 ? (
            <div className="divide-y">
              {requests.map((request) => (
                <div key={request.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`relative w-12 h-12 shrink-0 rounded-full flex items-center justify-center ${
                        request.status === 'pending' ? 'bg-yellow-100' :
                        request.status === 'approved' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {request.status === 'pending' ? (
                          <Clock className="w-6 h-6 text-yellow-600" />
                        ) : request.status === 'approved' ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                          <XCircle className="w-6 h-6 text-red-600" />
                        )}
                        <UnreadDot show={newThisVisit.includes(request.id)} />
                      </div>
                      <div>
                        {teamView && (
                          <p className="font-semibold text-[#1a3a5c]">{request.employee_name}</p>
                        )}
                        <p className="text-sm text-slate-600">
                          Du {format(new Date(request.start_date), 'd MMMM yyyy', { locale: fr })}
                          {' '}au {format(new Date(request.end_date), 'd MMMM yyyy', { locale: fr })}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                          {calculateDays(request.start_date, request.end_date)} jour(s) - {request.reason}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        request.status === 'pending' ? 'badge-pending' :
                        request.status === 'approved' ? 'badge-completed' : 'badge-cancelled'
                      }`}>
                        {request.status === 'pending' ? 'En attente' :
                         request.status === 'approved' ? 'Approuvé' : 'Refusé'}
                      </span>

                      {canApprove && request.status === 'pending' && request.employee_id !== user?.id && (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleAction(request.id, 'approved')}
                            data-testid={`approve-${request.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approuver
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600"
                            onClick={() => handleAction(request.id, 'rejected')}
                            data-testid={`reject-${request.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Refuser
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucune demande de congé</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Demander un congé</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Date de fin *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  min={formData.start_date}
                  required
                />
              </div>
            </div>

            {formData.start_date && formData.end_date && (
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <span className="font-semibold text-[#1a3a5c]">
                  {calculateDays(formData.start_date, formData.end_date)} jour(s)
                </span>
                <span className="text-slate-500"> demandé(s)</span>
              </div>
            )}

            <div>
              <Label>Raison *</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Décrivez la raison de votre demande..."
                required
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#00b4d8] hover:bg-[#009bb8]">
                Envoyer la demande
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveRequestsPage;
